'use client';

import * as React from 'react';

import { type ButtonProps, Button } from '@playbooks/ui/components/button';
import { controlChromeVariants } from '@playbooks/ui/components/component-recipes';
import { controlHeightClasses } from '@playbooks/ui/components/control-size';
import { Icon } from '@playbooks/ui/components/icon';
import { inputClassName } from '@playbooks/ui/components/input';
import { Spinner } from '@playbooks/ui/components/spinner';
import { cn } from '@playbooks/ui/lib/utils';

import { cva, VariantProps } from 'class-variance-authority';

const inputGroupVariants = cva(
	cn(
		controlChromeVariants(),
		'group/input-group relative flex w-full min-w-0 items-center focus-visible:border-border-strong focus-visible:ring-0 focus-within:border-focus focus-within:ring-3 focus-within:ring-focus/50 has-aria-invalid:border-danger has-aria-invalid:ring-3 has-aria-invalid:ring-danger/20 has-data-disabled:pointer-events-none has-data-disabled:bg-surface-hover has-data-disabled:opacity-50 dark:has-aria-invalid:border-danger/50 dark:has-aria-invalid:ring-danger/40',
	),
	{
		variants: {
			size: controlHeightClasses,
		},
		defaultVariants: {
			size: 'default',
		},
	},
);

type InputGroupProps = React.ComponentProps<'div'> & VariantProps<typeof inputGroupVariants>;
function InputGroup({ className, size, ...props }: InputGroupProps) {
	// Theme
	const rootClassName = cn(inputGroupVariants({ size }), className);

	// Render
	return <div data-slot='input-group' data-size={size || 'default'} className={rootClassName} {...props} />;
}

type InputGroupAddonProps = React.ComponentProps<'div'> & {
	align?: 'inline-start' | 'inline-end';
	isLoading?: boolean;
};

function InputGroupAddon({ className, align = 'inline-start', isLoading, children, ...props }: InputGroupAddonProps) {
	// Theme
	const rootClassName = cn(
		'flex shrink-0 items-center gap-1.5 text-sm text-foreground-body [&_svg]:pointer-events-none [&_svg]:size-3 [&_svg]:shrink-0',
		align === 'inline-start' ? 'order-first pl-3' : 'order-last ml-auto pr-3',
		className,
	);

	// Render
	return (
		<div data-slot='input-group-addon' data-align={align} className={rootClassName} {...props}>
			{isLoading ? <Spinner className='size-3.5' /> : children}
		</div>
	);
}

function InputGroupInput({ className, ...props }: React.ComponentProps<'input'>) {
	// Theme
	const rootClassName = cn(
		inputClassName,
		'h-full min-w-0 flex-1 rounded-none border-0 bg-transparent px-3 shadow-none focus-visible:border-transparent focus-visible:ring-0',
		'group-has-[[data-align=inline-start]]/input-group:pl-2 group-has-[[data-align=inline-end]]/input-group:pr-2',
		className,
	);

	// Render
	return <input data-slot='input-group-control' className={rootClassName} {...props} />;
}

function InputGroupText({ className, ...props }: React.ComponentProps<'span'>) {
	// Theme
	const rootClassName = cn('text-sm text-foreground-body', className);

	// Render
	return <span data-slot='input-group-text' className={rootClassName} {...props} />;
}

type InputGroupTagProps = React.ComponentProps<'span'> & {
	icon?: React.ReactNode;
	onRemove?: () => void;
	removeLabel?: string;
};

function InputGroupTag({
	children,
	className,
	icon,
	onRemove,
	removeLabel = 'Remove selection',
	...props
}: InputGroupTagProps) {
	// Theme
	const rootClassName = cn(
		'inline-flex h-6 min-w-0 max-w-full items-center gap-1 rounded-panel border border-border bg-surface-raised px-1.5 text-xs font-medium text-foreground shadow-xs',
		'[&>svg]:size-3 [&>svg]:shrink-0 [&>svg]:text-foreground-body',
		className,
	);

	// Render
	return (
		<span data-slot='input-group-tag' className={rootClassName} {...props}>
			{icon}
			<span className='truncate'>{children}</span>
			{onRemove ? (
				<InputGroupButton
					type='button'
					aria-label={removeLabel}
					onClick={onRemove}
					className='-mr-1 ml-0 size-5 shrink-0 p-0 text-foreground-body hover:text-foreground'>
					<Icon type='far' icon='xmark' aria-hidden />
				</InputGroupButton>
			) : null}
		</span>
	);
}

function InputGroupButton({ className, size = 'icon-xs', variant = 'ghost', ...props }: ButtonProps) {
	// Theme
	const rootClassName = cn('-mx-1', className);

	// Render
	return <Button data-slot='input-group-button' className={rootClassName} size={size} variant={variant} {...props} />;
}

type InputGroupTriggerProps = Omit<ButtonProps, 'size'> & {
	size?: NonNullable<InputGroupProps['size']>;
};

function InputGroupTrigger({
	className,
	children,
	nextIcon = 'chevron-down',
	size = 'default',
	...props
}: InputGroupTriggerProps) {
	// Theme
	const rootClassName = cn(
		inputGroupVariants({ size }),
		'w-full justify-start text-left [&_[data-icon=inline-end]]:ml-auto',
		className,
	);

	// Render
	return (
		<Button
			type='button'
			data-slot='input-group-trigger'
			variant='outline'
			size={size}
			nextIcon={nextIcon}
			className={rootClassName}
			{...props}>
			{children}
		</Button>
	);
}

export {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
	InputGroupTag,
	InputGroupText,
	InputGroupTrigger,
};
export { inputGroupVariants };
export type { InputGroupAddonProps, InputGroupProps, InputGroupTagProps, InputGroupTriggerProps };
